import React, { memo } from "react";
import { TouchableOpacity, View } from "react-native";
import { AppText } from "@/components/ui/AppText";
import { useI18n } from "@/context/I18nContext";
import type { TranslationKeys } from "@chinese-vendor/i18n";
import { Icon } from "@/components/ui/Icon";
import { Feather } from "@expo/vector-icons";

export type TagVariant = "selected" | "add" | "toggle";

export interface TagProps {
  /** Text label displayed inside the tag. */
  label: TranslationKeys;
  variant?: TagVariant;
  /**
   * Called when the × remove button is pressed.
   * Only relevant for the `selected` variant.
   */
  onRemove?: () => void;
  /**
   * Called when the entire tag is pressed.
   * Used by the `add` variant; ignored on `selected` unless set.
   */
  onPress?: () => void;
  /** Forwarded to the root element for testing. */
  testID?: string;
  /** Screen-reader label (translation key). Defaults to `label`. */
  accessibilityLabel?: TranslationKeys;
  /**
   * Custom icon for the remove button in `selected` variant.
   * Defaults to an "×" character rendered via AppText.
   */
  removeIcon?: React.ReactNode;
  /**
   * Custom icon for the `add` variant shown left of the label.
   * Defaults to a "+" character rendered via AppText.
   */
  addIcon?: React.ReactNode;
  /** Visual selected state for the `toggle` variant. */
  selected?: boolean;
  /**
   * Raw display string that bypasses the i18n `t()` call.
   * Use this for dynamically loaded content (e.g. API-returned names).
   * When provided, this value is used for both the visible label and the
   * accessibility label; `label` is only used for screen readers if
   * `accessibilityLabel` is explicitly set.
   */
  rawLabel?: string;
}

// ─── Variant style maps ──────────────────────────────────────────────────────

const CONTAINER_STYLES: Record<TagVariant, string> = {
  // Figma: bg 'Blue/05', border 'Blue/10', pill shape
  selected: "bg-blue-5 border border-blue-10 rounded-full flex-row items-center gap-2 px-4 py-2",
  // Figma: bg white, border 'Stroke/50', rounded rect
  add: "bg-white border border-stroke-50 rounded-lg flex-row items-center gap-2 px-4 py-3",
  toggle: "rounded-full border px-4 py-2",
};

const LABEL_COLOR: Record<TagVariant, string> = {
  selected: "text-supporting-text", // #3D414D — Figma: Supporting Text/50
  add: "text-primary", // #FF1654 — Figma: Main Color/50
  toggle: "text-dark-text-20",
};

const TOGGLE_STYLES = {
  selectedContainer: "bg-primary border-primary",
  idleContainer: "bg-white border-stroke-50",
  selectedLabel: "text-white",
  idleLabel: "text-dark-text-20",
};

// ─── Component ───────────────────────────────────────────────────────────────

/**
 * Tag / chip component with two visual variants extracted from the Figma design:
 *
 * - `selected` — blue pill showing an active selection with a × remove button.
 * - `add`      — outlined pill with a + icon for triggering item selection.
 *
 * All text is rendered via `AppText` so the locale font is applied automatically.
 * Icon slots accept arbitrary `React.ReactNode` so you can pass SVG/image icons.
 */
export const Tag = memo(function Tag({
  label,
  variant = "selected",
  onRemove,
  onPress,
  testID,
  accessibilityLabel,
  removeIcon,
  addIcon,
  selected = false,
  rawLabel,
}: TagProps) {
  const { t } = useI18n();
  const containerClass = CONTAINER_STYLES[variant];
  const labelColor = LABEL_COLOR[variant];
  const a11yLabel = accessibilityLabel
    ? t(accessibilityLabel)
    : (rawLabel ?? t(label) ?? t("ui.tag.addA11y"));
  const displayLabel = rawLabel ?? t(label);

  if (variant === "add") {
    return (
      <TouchableOpacity
        onPress={onPress}
        testID={testID}
        accessibilityRole="button"
        accessibilityLabel={a11yLabel}
        className={containerClass}
        activeOpacity={0.7}
      >
        {addIcon != null ? (
          addIcon
        ) : (
          <Icon
            as={Feather}
            name="plus"
            size={16}
            className="text-primary"
            accessibilityElementsHidden
            importantForAccessibility="no"
          />
        )}
        <AppText weight="medium" className={`text-base ${labelColor}`}>
          {displayLabel}
        </AppText>
      </TouchableOpacity>
    );
  }

  if (variant === "toggle") {
    return (
      <TouchableOpacity
        onPress={onPress}
        testID={testID}
        accessibilityRole="button"
        accessibilityState={{ selected }}
        accessibilityLabel={a11yLabel}
        className={`rounded-full border px-4 py-2 ${
          selected ? TOGGLE_STYLES.selectedContainer : TOGGLE_STYLES.idleContainer
        }`}
        activeOpacity={0.7}
      >
        <AppText
          weight="medium"
          className={`text-sm ${selected ? TOGGLE_STYLES.selectedLabel : TOGGLE_STYLES.idleLabel}`}
        >
          {displayLabel}
        </AppText>
      </TouchableOpacity>
    );
  }

  // ── selected variant ───────────────────────────────────────────────────────
  return (
    <View testID={testID} accessibilityLabel={a11yLabel} className={containerClass}>
      <AppText weight="medium" className={`text-base ${labelColor}`}>
        {displayLabel}
      </AppText>
      {onRemove && (
        <TouchableOpacity
          onPress={onRemove}
          hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
          accessibilityRole="button"
          accessibilityLabel={t("ui.tag.removeA11y", { label })}
          activeOpacity={0.7}
        >
          {removeIcon != null ? (
            removeIcon
          ) : (
            <Icon as={Feather} name="x" size={16} className="text-dark-text-15" />
          )}
        </TouchableOpacity>
      )}
    </View>
  );
});
